import React, { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { CartProvider, useCart } from './context/CartContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { CartDrawer } from './components/cart/CartDrawer'
import { CartIcon } from './components/common/CartIcon'
import { WalletIcon } from './components/common/WalletIcon'
import { Footer } from './components/Footer'
import { useAuth } from './hooks/useAuth'
import { useWalletBalance } from './hooks/useWalletBalance'
import { captureUTMParams, clearUserId, setUserId, trackPageView } from './utils/analytics'
import { Landing } from './pages/Landing'   // eager — the entry/hero route

// Secondary routes are code-split so their Three.js/GSAP code isn't parsed up
// front; each loads on demand behind a themed fallback (smoother navigation).
const Formulation   = lazy(() => import('./pages/Formulation'))
const Orders        = lazy(() => import('./pages/Orders'))
const Login         = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })))
const Signup        = lazy(() => import('./pages/Signup').then(m => ({ default: m.Signup })))
const Profile       = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })))
const Shop          = lazy(() => import('./pages/Shop').then(m => ({ default: m.Shop })))
const ProductDetail = lazy(() => import('./pages/ProductDetail').then(m => ({ default: m.ProductDetail })))
const Checkout      = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })))
const PaymentStatus = lazy(() => import('./pages/PaymentStatus').then(m => ({ default: m.PaymentStatus })))

// Static legal / info pages — public, no auth. Code-split so their content
// isn't parsed until visited.
const Terms   = lazy(() => import('./pages/Terms'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Refund  = lazy(() => import('./pages/Refund'))
const Contact = lazy(() => import('./pages/Contact'))
const About   = lazy(() => import('./pages/About'))

// Pre-launch waitlist — public, no auth. Code-split like the other info pages.
const ComingSoon = lazy(() => import('./pages/ComingSoon'))

function PageFallback() {
  return (
    <div style={{ minHeight: '100vh', background: '#0f0c0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '50%',
        border: '2px solid rgba(232,220,192,0.22)', borderTopColor: '#e8dcc0',
        animation: 'tpc-route-spin 0.8s linear infinite',
      }} />
    </div>
  )
}

// Catch-all for unknown URLs — themed like the rest of the app, offers the
// two paths that matter: home and the shop.
function NotFound() {
  return (
    <div style={{
      minHeight: '100vh', background: '#0f0c0a', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '18px', padding: '24px',
      textAlign: 'center', fontFamily: "'Fira Code', monospace",
    }}>
      <p style={{ fontSize: '11px', letterSpacing: '0.42em', textTransform: 'uppercase', color: 'rgba(232,220,192,0.75)', margin: 0 }}>
        Lost in the sillage
      </p>
      <h1 style={{ fontSize: 'clamp(2rem,6vw,3.2rem)', fontWeight: 300, color: '#e8e8f0', margin: 0, letterSpacing: '-0.015em' }}>
        This page has <span style={{ color: '#e8dcc0', fontStyle: 'italic' }}>evaporated.</span>
      </h1>
      <p style={{ fontSize: '0.95rem', color: '#8a8a9a', margin: '0 0 10px' }}>
        The scent trail ends here — let's take you somewhere real.
      </p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link to="/" style={{
          padding: '13px 32px', borderRadius: '50px', textDecoration: 'none',
          background: 'linear-gradient(180deg, #f7f4ea, #e6dfcd)', color: '#17181c', fontWeight: 700, fontSize: '0.9rem',
        }}>Back to the story</Link>
        <Link to="/shop" style={{
          padding: '13px 28px', borderRadius: '50px', textDecoration: 'none',
          border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem',
        }}>Explore the collection</Link>
      </div>
    </div>
  )
}

// Thin gold progress bar shown while a route change is in flight. Lazy routes
// need a moment to fetch their chunk, and until then the user sees nothing at
// all — this gives immediate feedback without pulling in a router library.
// Deliberately not rendered on the first paint: only actual navigations.
function RouteProgress() {
  const { pathname } = useLocation()
  const [phase, setPhase] = React.useState<'idle' | 'loading' | 'done'>('idle')
  const first = React.useRef(true)

  React.useEffect(() => {
    if (first.current) { first.current = false; return }
    setPhase('loading')
    // The new route's component has mounted by the time this effect's timers
    // run out; 'done' snaps the bar to full width and fades it away.
    const doneT = setTimeout(() => setPhase('done'), 450)
    const idleT = setTimeout(() => setPhase('idle'), 800)
    return () => { clearTimeout(doneT); clearTimeout(idleT) }
  }, [pathname])

  if (phase === 'idle') return null

  return (
    <div style={s.progressTrack} aria-hidden="true">
      <div style={{
        ...s.progressBar,
        transform: phase === 'done' ? 'scaleX(1)' : undefined,
        opacity: phase === 'done' ? 0 : 1,
        animation: phase === 'loading' ? 'tpc-route-progress 0.45s ease-out forwards' : 'none',
      }} />
    </div>
  )
}

function Nav() {
  const { user, logout }   = useAuth()
  const { cart, openCart } = useCart()
  // Store credits, so a grant is discoverable from anywhere rather than only if
  // the customer happens to open their profile or reach checkout.
  const creditBalance      = useWalletBalance()
  const navigate           = useNavigate()
  const location           = useLocation()
  const isLanding          = location.pathname === '/'
  const navRef             = React.useRef<HTMLElement>(null)

  // 900px, not 768: the logged-in desktop link row (Shop/Craft/Orders/Profile/
  // Cart/name/Sign out) overflows below ~900px and clips off-screen — switch to
  // the hamburger before that can happen.
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  )
  const [menuOpen, setMenuOpen] = React.useState(false)

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // Close the mobile menu whenever the route changes.
  React.useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // The nav is position:fixed, so it contributes no height to the document and
  // every page has to reserve that space itself. Publish the measured height as
  // --tpc-nav-h and let the pages offset against it, so the content below never
  // shifts when the height changes — which it does on the first paint (the
  // Rockwell wordmark swaps in), on resize, and when the mobile menu opens.
  React.useEffect(() => {
    const el = navRef.current
    if (!el) return
    const publish = () => {
      // Exclude the expanded mobile panel: it's absolutely positioned below the
      // bar, so it must not push the page content down when it opens.
      const h = el.getBoundingClientRect().height
      document.documentElement.style.setProperty('--tpc-nav-h', `${Math.round(h)}px`)
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  async function handleLogout() {
    setMenuOpen(false)
    await logout()
    navigate('/login', { replace: true })
  }

  const close = () => setMenuOpen(false)
  const linkStyle = isMobile ? s.linkMobile : s.link

  const cartButton = (
    <button onClick={() => { openCart(); close() }} style={s.cartBtn} aria-label="Open cart">
      <CartIcon />
      <span>Cart</span>
      {cart.item_count > 0 && <span style={s.cartBadge}>{cart.item_count}</span>}
    </button>
  )

  // Shared link set — rendered in a row on desktop, stacked in the mobile panel.
  const navLinks = (
    <>
      <Link to="/shop"  style={linkStyle} onClick={close}>Shop</Link>
      <Link to="/craft" style={linkStyle} onClick={close}>Craft</Link>
      {user ? (
        <>
          <Link to="/orders"  style={linkStyle} onClick={close}>Orders</Link>
          <Link to="/profile" style={linkStyle} onClick={close}>Profile</Link>
          {/* Only when there is something to spend. A permanent "₹0" pill is
              clutter, not a nudge — the profile is where an empty wallet is
              explained, and this chip exists to point at a balance that
              actually exists. Links there, since that's where the history is. */}
          {creditBalance > 0 && (
            <Link
              to="/profile"
              style={s.creditChip}
              onClick={close}
              title="Your store credits — spend them at checkout"
            >
              {/* Same mark as the profile badge — the chip and the badge are
                  meant to read as one object seen twice. */}
              <WalletIcon size={13} />
              <span>₹{creditBalance.toLocaleString('en-IN')}</span>
            </Link>
          )}
          {cartButton}
          <span style={s.userName}>{user.display_name || user.email?.split('@')[0] || 'You'}</span>
          <button onClick={handleLogout} style={s.logoutBtn}>Sign out</button>
        </>
      ) : (
        <>
          {cartButton}
          <Link to="/login"  style={linkStyle} onClick={close}>Sign in</Link>
          <Link to="/signup" style={s.signupBtn} onClick={close}>Get started</Link>
        </>
      )}
    </>
  )

  return (
    <nav ref={navRef} style={{
      ...s.nav,
      padding: isMobile ? '7px 20px' : '8px 32px',
      // A fixed floor keeps the bar from resizing when the Rockwell wordmark
      // finishes loading and swaps in (font-display: swap), which was one of
      // the sources of the content jump on first paint. On mobile this also
      // matches the 40px hamburger plus padding, so the two agree.
      minHeight: isMobile ? '54px' : '62px',
      // No blur on the landing page so the animated brand mark stays crisp.
      backdropFilter: isLanding && !menuOpen ? 'none' : 'blur(20px)',
      WebkitBackdropFilter: isLanding && !menuOpen ? 'none' : 'blur(20px)',
      background: isLanding
        ? (isMobile && menuOpen ? 'rgba(11,8,6,0.97)' : 'rgba(0,0,0,0.1)')
        : 'rgba(0,0,0,0.7)',
    }}>
      {/* Brand — the landing wordmark in its post-scroll corner state: a
          left-aligned three-line stack (THE / PERFUME / CULTURE). Hidden on
          the landing page, where the animated brand mark (Landing.tsx) plays
          that morph itself and settles into exactly this lockup. */}
      <Link to="/" style={{ ...s.logo, visibility: isLanding ? 'hidden' : 'visible' }} aria-hidden={isLanding} aria-label="The Perfume Culture">
        <span style={{ ...s.logoText, fontSize: isMobile ? '9px' : '11px' }}>
          <span style={s.logoLine}>The</span>
          <span style={s.logoLine}>Perfume</span>
          <span style={s.logoLine}>Culture</span>
        </span>
      </Link>

      {isMobile ? (
        <>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={s.hamburger}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span style={{ ...s.bar, transform: menuOpen ? 'translateY(6px) rotate(45deg)' : 'none' }} />
            <span style={{ ...s.bar, opacity: menuOpen ? 0 : 1 }} />
            <span style={{ ...s.bar, transform: menuOpen ? 'translateY(-6px) rotate(-45deg)' : 'none' }} />
          </button>
          {menuOpen && <div style={s.mobilePanel}>{navLinks}</div>}
        </>
      ) : (
        <div style={s.links}>{navLinks}</div>
      )}
    </nav>
  )
}

// Routes that render as a standalone, chrome-free page — no global nav or
// footer (e.g. the pre-launch waitlist). Rendered inside <BrowserRouter> so
// these wrappers can read the current location.
const BARE_ROUTES = ['/coming-soon']

function ChromeNav() {
  const location = useLocation()
  return BARE_ROUTES.includes(location.pathname) ? null : <Nav />
}

function ChromeFooter() {
  const location = useLocation()
  return BARE_ROUTES.includes(location.pathname) ? null : <Footer />
}

// Client-side (<Link>) navigation keeps the window's scroll position across
// route changes — unlike a full page load, which always starts at the top. This
// resets scroll to the top whenever the path changes so a new page never opens
// mid-way down (e.g. clicking a footer link from the bottom of a long page).
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// Human-readable titles for the routes GA4 reports on. GA4 groups pages by
// title in most of its default reports, so leaving this to the document title
// (which never changes — index.html sets one for the whole SPA) would collapse
// every route into a single row.
const GA_PAGE_TITLES: Record<string, string> = {
  '/':               'Home — The Perfume Culture',
  '/craft':          'Craft Your Perfume — TPC',
  '/formulation':    'Craft Your Perfume — TPC',
  '/shop':           'Shop — TPC',
  '/checkout':       'Checkout — TPC',
  '/orders':         'My Orders — TPC',
  '/profile':        'My Profile — TPC',
  '/login':          'Sign In — TPC',
  '/signup':         'Sign Up — TPC',
  '/about':          'About — TPC',
  '/contact':        'Contact — TPC',
  '/coming-soon':    'Coming Soon — TPC',
  '/payment/status': 'Payment Status — TPC',
  '/terms':          'Terms — TPC',
  '/privacy':        'Privacy — TPC',
  '/refund':         'Refunds — TPC',
}

function gaPageTitle(pathname: string): string {
  const known = GA_PAGE_TITLES[pathname]
  if (known) return known
  // Product ids are opaque UUIDs — reporting them as distinct titles would
  // shatter the catalogue into one row per product with no way to read it.
  // The per-product signal is `view_item`, which carries the real name.
  if (pathname.startsWith('/products/')) return 'Product — TPC'
  return 'The Perfume Culture'
}

// GA4's tag is configured with send_page_view: false (public/index.html), so
// this is what actually reports navigation. Renders nothing — it exists purely
// for its effects, and sits inside AuthProvider because it needs auth state.
function GAPageTracker() {
  const location = useLocation()
  const { user, isLoading } = useAuth()

  useEffect(() => {
    trackPageView(location.pathname, gaPageTitle(location.pathname))
    // Campaign params only ever ride on the landing URL, and a client-side
    // navigation strips the query string — so this is effectively a first-hit
    // capture. Calling it per route change costs nothing and also catches a
    // hard reload onto a deep link that carried UTMs.
    captureUTMParams()
  }, [location.pathname])

  // Identity, once auth has actually resolved. Deliberately gated on
  // isLoading: the session is restored from the refresh cookie asynchronously,
  // so acting on the null user of the first render would clear the id on every
  // page load a moment before setting it again.
  useEffect(() => {
    if (isLoading) return
    if (user?.id) setUserId(user.id)
    else clearUserId()
  }, [user?.id, isLoading])

  return null
}

const App = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteProgress />
      <AuthProvider>
        <GAPageTracker />
        <CartProvider>
          <ChromeNav />
          <CartDrawer />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/"               element={<Landing />} />
            <Route path="/shop"           element={<Shop />} />
            <Route path="/products/:id"   element={<ProductDetail />} />
            <Route path="/login"          element={<Login />} />
            <Route path="/signup"         element={<Signup />} />
            <Route path="/payment/status" element={<PaymentStatus />} />
            <Route path="/coming-soon"    element={<ComingSoon />} />

            {/* Legal / info — publicly accessible, no auth */}
            <Route path="/terms"   element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund"  element={<Refund />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about"   element={<About />} />

            {/* The formulation quiz is PUBLIC: a guest completes it, sees their
                formula and adds it to a local bag (utils/guestCart). Sign-in is
                asked for once, at the payment step. */}
            <Route path="/craft"       element={<Formulation />} />
            <Route path="/formulation" element={<Formulation />} />

            {/* Protected — everything that needs an account to mean anything:
                paying, order history, and the profile itself. */}
            <Route path="/checkout" element={
              <ProtectedRoute><Checkout /></ProtectedRoute>
            }/>
            <Route path="/orders" element={
              <ProtectedRoute><Orders /></ProtectedRoute>
            }/>
            <Route path="/profile" element={
              <ProtectedRoute><Profile /></ProtectedRoute>
            }/>

            {/* Unknown URLs land on a themed 404 instead of a blank screen. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <ChromeFooter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

const s: Record<string, React.CSSProperties> = {
  // Sits above the nav (z-index 1000) so it reads as page-level chrome.
  progressTrack: {
    position: 'fixed', top: 0, left: 0, right: 0, height: '2px',
    zIndex: 1100, pointerEvents: 'none', background: 'transparent',
  },
  progressBar: {
    height: '100%', width: '100%', transformOrigin: 'left center',
    background: 'linear-gradient(90deg, rgba(232,220,192,0.2), #e8dcc0)',
    boxShadow: '0 0 10px rgba(232,220,192,0.55)',
    transform: 'scaleX(0)',
    transition: 'transform 0.25s ease-out, opacity 0.3s ease 0.15s',
  },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 32px',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    fontFamily: "'Fira Code', monospace",
    transition: 'background 0.3s ease'
  },
  // The brand may shrink and ellipsize, but it must NEVER push the hamburger
  // or links off-screen (device font scaling inflates the wordmark on phones).
  logo: {
    display: 'flex', alignItems: 'center',
    textDecoration: 'none', padding: '3px 0',
    flex: '0 1 auto', minWidth: 0, overflow: 'hidden', marginRight: '12px'
  },
  logoText: {
    fontWeight: 700, color: '#ece2cd',
    letterSpacing: '0.3em', textTransform: 'uppercase',
    display: 'block',
    textShadow: '0 2px 18px rgba(0,0,0,0.7), 0 0 30px rgba(232,220,192,0.2)',
    fontFamily: "'Rockwell Condensed', 'Fira Code', monospace"
  },
  // Three stacked lines set the bar's height, so the leading here is the single
  // biggest lever on it. 1.2 matches the landing page's animated wordmark,
  // which settles into exactly this lockup.
  logoLine: { display: 'block', lineHeight: 1.2, whiteSpace: 'nowrap' },
  links: { display: 'flex', gap: '12px 20px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
  hamburger: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    gap: '5px', width: '40px', height: '40px', flexShrink: 0,
    background: 'none', border: 'none', cursor: 'pointer', padding: '8px',
  },
  bar: {
    display: 'block', width: '22px', height: '2px',
    background: '#e8e8f0', borderRadius: '2px',
    transition: 'transform 0.3s ease, opacity 0.2s ease',
  },
  mobilePanel: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px',
    padding: '10px 20px 22px',
    background: 'rgba(11,8,6,0.97)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  linkMobile: {
    fontSize: '13px', color: 'rgba(255,255,255,0.78)', fontWeight: 500,
    textDecoration: 'none', letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '15px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  link: {
    fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 500,
    textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase'
  },
  cartBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    background: 'none', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '50px', padding: '8px 14px',
    color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
    fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase',
    position: 'relative'
  },
  cartBadge: {
    background: '#e8dcc0', color: '#0f0c0a',
    borderRadius: '50%', width: '16px', height: '16px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '9px', fontWeight: 700
  },
  // Reads as a value the customer holds, so it borrows the gold the balance is
  // printed in on the profile — not the plain link colour of the row around it.
  creditChip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
    color: '#c9a96e', textDecoration: 'none',
    background: 'rgba(201,169,110,0.1)',
    border: '1px solid rgba(201,169,110,0.3)',
    borderRadius: '50px', padding: '5px 11px',
    whiteSpace: 'nowrap',
  },
  userName: {
    fontSize: '11px', color: 'rgba(232,220,192,0.8)',
    fontWeight: 500, letterSpacing: '0.05em'
  },
  logoutBtn: {
    fontSize: '11px', color: 'rgba(255,255,255,0.3)',
    background: 'none', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '50px', padding: '6px 14px', cursor: 'pointer',
    letterSpacing: '0.05em', textTransform: 'uppercase'
  },
  signupBtn: {
    fontSize: '11px', color: '#17181c', fontWeight: 700,
    background: 'linear-gradient(180deg, #f7f4ea, #e6dfcd)',
    borderRadius: '50px', padding: '9px 18px',
    textDecoration: 'none', letterSpacing: '0.08em',
    textTransform: 'uppercase', textAlign: 'center'
  }
}

export default App